
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Code, Bot, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import EditableText from './cms/EditableText';
import EditableImage from './cms/EditableImage';
import developerCharacter from '@/assets/developer-character.png';
import aiTechnicianCharacter from '@/assets/ai-technician-character.png';
import salesCharacter from '@/assets/sales-character.png';

const JoinUsSection = () => {
  const { t } = useLanguage();
  
  const positions = [{
    titleKey: 'joinus.freelanceDeveloper',
    descriptionKey: 'joinus.developerDesc',
    learnMoreKey: 'joinus.learnMoreDeveloper',
    icon: Code,
    image: developerCharacter,
    link: "/jobs/freelance-developer"
  }, {
    titleKey: 'joinus.freelanceAITechnician',
    descriptionKey: 'joinus.aiTechnicianDesc',
    learnMoreKey: 'joinus.learnMoreAITechnician',
    icon: Bot,
    image: aiTechnicianCharacter,
    link: "/jobs/freelance-ai-technician"
  }, {
    titleKey: 'joinus.freelanceSalesPerson',
    descriptionKey: 'joinus.salesPersonDesc',
    learnMoreKey: 'joinus.learnMoreSales',
    icon: Briefcase,
    image: salesCharacter,
    link: "/jobs/freelance-sales-person"
  }];

  return (
    <section id="joinus" className="py-20 px-4 relative overflow-hidden">
      {/* Background Elements - continuing the pattern from Our Works section */}
      <div className="absolute inset-0">
        <div className="float absolute top-20 right-32 w-28 h-28 bg-primary/15 rounded-full blur-xl" style={{
          animationDelay: '1s'
        }}></div>
        <div className="float absolute bottom-32 left-32 w-20 h-20 bg-accent/25 rounded-full blur-2xl" style={{
          animationDelay: '3s'
        }}></div>
        <div className="float absolute top-1/2 left-1/4 w-16 h-16 bg-secondary/10 rounded-full blur-lg" style={{
          animationDelay: '4s'
        }}></div>
      </div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="p-2 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 backdrop-blur-sm">
              <Users className="h-8 w-8 text-primary" aria-hidden />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
              <EditableText k="joinus.joinUs" />
            </h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            <EditableText k="joinus.lookingForTalented" />
          </p>
        </div>

        {/* Join Us cards - responsive grid layout */}
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
            {positions.map((position, idx) => (
              <Card
                key={idx}
                className={`group h-full overflow-hidden transition-all duration-500 shadow-sm hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1 bg-card/70 backdrop-blur-sm border border-border/50 hover:border-primary/30 rounded-2xl animate-fade-in ${
                  idx === 2 && positions.length === 3 ? 'lg:col-span-2 xl:col-span-1 lg:mx-auto lg:max-w-md xl:max-w-none' : ''
                }`}
                style={{ animationDelay: `${idx * 120}ms` }}
              >
                <div className="flex flex-col h-full">
                  {/* Top: character image */}
                  <div className="relative h-64 lg:h-72 bg-muted/40 border-b border-border/50 flex items-center justify-center p-6 overflow-hidden">
                    <EditableImage 
                      src={position.image} 
                      alt={`Character illustration`}
                      className="w-full h-full object-contain"
                      imageKey={`character_${idx}`}
                      folder="characters"
                    />
                  </div>
                  
                  {/* Bottom: text content */}
                  <div className="flex flex-col flex-1 p-6 gap-4">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-xl font-semibold group-hover:text-primary transition-colors duration-300">
                        <EditableText k={position.titleKey} />
                      </CardTitle>
                    </div>
                    <CardDescription className="text-muted-foreground leading-relaxed text-sm flex-1">
                      <EditableText k={position.descriptionKey} />
                    </CardDescription>
                    <div className="mt-auto">
                      <Link 
                        to={position.link} 
                        className="block"
                      >
                        <Button
                          size="lg"
                          className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary hover:shadow-lg hover:shadow-primary/25 transition-all duration-300"
                        >
                          <EditableText k={position.learnMoreKey} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JoinUsSection;
